import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  AccessPointEdgeHubService,
  EMBEDDED_ENROLL_STAGE_PERCENT,
  EmbeddedEnrollStage,
} from '@settings/data-access';
import {
  EMPTY,
  from,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
  TimeoutError,
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { catchError } from 'rxjs/operators';
import { FingerPosition } from './entities/profile-biometrics.model';
import {
  EmbeddedEnrollOutcome,
  EmbeddedEnrollResponse,
  EmbeddedTemplate,
  isEmbeddedEnrollSuccess,
} from './entities/profile-embedded-biometrics.model';
import { ProfileEmbeddedBiometricsService } from './infra/profile-embedded-biometrics.service';

interface IProfileEmbeddedBiometricsStore {
  /** Equipamento cujo grupo está assinado. Guardado para desassinar ao sair. */
  edgeId: string | null;
  /**
   * Correlação deste cadastro. É por ele que o progresso alheio é descartado.
   *
   * Gerado no front, e não pelo backend, por uma questão de ordem: o progresso
   * começa a chegar antes de o POST responder, portanto um id que só viesse na
   * resposta chegaria tarde demais para filtrar seja o que for.
   */
  requestId: string | null;
  /** O POST de cadastro já partiu — só a partir daí há o que cancelar na placa. */
  commandSent: boolean;
  /**
   * O atendente pediu para cancelar. Fica ligado até o cadastro fechar, para
   * que um segundo clique não repita o pedido e para que um cancelamento feito
   * antes de o POST partir o impeça de partir.
   */
  cancelRequested: boolean;
  stage: EmbeddedEnrollStage | null;
  percent: number;
  outcome: string | null;
  /** Texto do backend para desfecho desconhecido. Não é chave i18n. */
  outcomeMessage: string | null;
  templates: EmbeddedTemplate[];
}

const initialState: IProfileEmbeddedBiometricsStore = {
  edgeId: null,
  requestId: null,
  commandSent: false,
  cancelRequested: false,
  stage: null,
  percent: 0,
  outcome: null,
  outcomeMessage: null,
  templates: [],
};

/**
 * Cadastro de digital no sensor embarcado da catraca.
 *
 * ## Porque é uma store separada da `ProfileBiometricsStore`
 *
 * Não é duplicação: o ciclo de vida diverge por inteiro. Aqui não existe
 * `lastCapture`, `pendingTemplates`, `validateLast` nem `compareWith` — a placa
 * faz as três leituras sozinha e o front só assiste. E a lista de cadastrados
 * tem outra chave (`EmbeddedTemplateId`, não `templateId`), noutro serviço.
 * Fundir as duas deixaria metade do estado morto em cada caminho, e obrigaria a
 * mexer numa store que hoje corre em produção sem defeito conhecido.
 *
 * ## A ordem do cadastro é parte do contrato
 *
 * `startHub` → `SubscribeEdge` → `POST`. A placa começa a preparar o sensor mal
 * recebe o comando, e isso leva cerca de 25 segundos; assinar depois do POST
 * perde as primeiras etapas. O `beginEnroll` encadeia os três para que nenhum
 * componente possa trocar a ordem por engano.
 *
 * ## O progresso não decide nada
 *
 * As notificações são melhor esforço: uma perdida não invalida o cadastro. A
 * autoridade é a resposta do POST, e é só nela que a UI assenta.
 *
 * ## Cancelar não fecha o cadastro
 *
 * O `cancelEnroll` só manda a placa parar de esperar o dedo. Quem fecha o ciclo
 * continua a ser o POST de cadastro, que responde `Cancelled` — ou `Enrolled`,
 * se a placa terminou antes de o pedido chegar, e nesse caso a digital ficou
 * gravada e a UI tem de o dizer. Por isso o cancelamento não mexe no desfecho.
 */
export const ProfileEmbeddedBiometricsStore = signalStore(
  { providedIn: 'root' },
  withState<IProfileEmbeddedBiometricsStore>(initialState),
  withReqState('enroll', 'cancelEnroll', 'loadTemplates', 'revokeTemplate'),
  withComputed((store) => ({
    /** Dedos já cadastrados na catraca, para o seletor de mão marcar. */
    enrolledFingers: computed(() =>
      store.templates().map((template) => template.FingerPosition)
    ),
    isSuccess: computed(() => isEmbeddedEnrollSuccess(store.outcome())),
    /**
     * `SavedPendingWrite` é sucesso, mas com uma ressalva que o atendente precisa
     * de ver: o servidor já tem a digital e a catraca só a recebe na próxima
     * sincronização.
     */
    isPendingWrite: computed(
      () => store.outcome() === EmbeddedEnrollOutcome.SavedPendingWrite
    ),
  })),
  withMethods(
    (
      store,
      service = inject(ProfileEmbeddedBiometricsService),
      hubService = inject(AccessPointEdgeHubService),
      errorStore = inject(ErrorStore)
    ) => {
      // Recebe o par já fotografado, e não o lê do estado: ao fechar o modal o
      // `stopWatching` apaga-o logo a seguir. `mergeMap` porque dois pedidos de
      // cadastros diferentes (um timeout e um fecho) não se anulam um ao outro.
      const sendCancel = rxMethod<{ edgeId: string; requestId: string }>(
        pipe(
          tap(() => patchState(store, setPropLoading('cancelEnroll'))),
          mergeMap(({ edgeId, requestId }) =>
            service.cancelEnroll(edgeId, { RequestId: requestId }).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('cancelEnroll')),
                error: (error: HttpErrorResponse) => {
                  // A catraca segue à espera até ao timeout dela; o atendente
                  // precisa de saber, e pode tentar cancelar de novo.
                  patchState(
                    store,
                    { cancelRequested: false },
                    setPropError('cancelEnroll', error)
                  );
                  errorStore.setHttpError(error);
                },
              })
            )
          )
        )
      );

      return {
        loadTemplates: rxMethod<string>(
          pipe(
            tap(() => patchState(store, setPropLoading('loadTemplates'))),
            switchMap((personId) =>
              service.listTemplates(personId).pipe(
                tapResponse({
                  next: (templates) =>
                    patchState(
                      store,
                      { templates },
                      setPropLoaded('loadTemplates')
                    ),
                  error: (error: HttpErrorResponse) => {
                    // A lista anterior fica: apagá-la faria o atendente ver
                    // "nenhuma digital" e recadastrar um dedo que já existe.
                    patchState(store, setPropError('loadTemplates', error));
                    errorStore.setHttpError(error);
                  },
                })
              )
            )
          )
        ),

        beginEnroll: rxMethod<{
          edgeId: string;
          personId: string;
          finger: FingerPosition;
        }>(
          pipe(
            tap(({ edgeId }) => {
              // `personId` e `finger` chegam por argumento e seguem no corpo do
              // pedido; guardá-los no estado só criava duas cópias que ninguém lê.
              patchState(store, {
                edgeId,
                requestId: uuid(),
                commandSent: false,
                cancelRequested: false,
                stage: null,
                percent: 0,
                outcome: null,
                outcomeMessage: null,
              });
              patchState(
                store,
                setPropLoading('enroll'),
                setPropInit('cancelEnroll')
              );
            }),
            switchMap(({ edgeId, personId, finger }) =>
              // Assinar antes do POST, e não em paralelo: `from` só emite quando a
              // assinatura resolve, e só então o comando parte.
              from(hubService.start().then(() => hubService.subscribeEdge(edgeId))).pipe(
                // Falhar a assinar não impede o cadastro — só perde o progresso.
                // Deixar cair aqui seria trocar uma barra parada por um cadastro
                // que nunca acontece.
                catchError(() => of(void 0)),
                switchMap(() => {
                  const requestId = store.requestId();
                  if (!requestId) return EMPTY;
                  // Cancelado enquanto o hub ainda ligava: a placa nunca soube do
                  // cadastro, portanto o desfecho é local e nada parte para ela.
                  if (store.cancelRequested()) {
                    patchState(
                      store,
                      { outcome: EmbeddedEnrollOutcome.Cancelled, percent: 0 },
                      setPropError('enroll', EmbeddedEnrollOutcome.Cancelled)
                    );
                    return EMPTY;
                  }
                  patchState(store, { commandSent: true });
                  return service
                    .enroll(edgeId, {
                      PersonId: personId,
                      FingerPosition: finger,
                      RequestId: requestId,
                    })
                    .pipe(
                      tapResponse({
                        next: (response: EmbeddedEnrollResponse) => {
                          // Modal fechado entretanto: a resposta é de um cadastro
                          // que já ninguém acompanha.
                          if (store.requestId() !== requestId) return;
                          patchState(
                            store,
                            {
                              outcome: response.Outcome,
                              outcomeMessage: response.Message ?? null,
                              // O progresso é melhor esforço e pode ter perdido a
                              // última etapa; um 200 fecha a barra na mesma.
                              percent: 100,
                            },
                            setPropLoaded('enroll')
                          );
                        },
                        error: (error: HttpErrorResponse) => {
                          // O nosso timeout só larga o pedido do lado do browser;
                          // sem isto a catraca continuava à espera do dedo.
                          if (error instanceof TimeoutError) {
                            sendCancel({ edgeId, requestId });
                          }
                          if (store.requestId() !== requestId) return;
                          // 409 é a recusa esperada e traz o motivo no corpo:
                          // vai para erro inline com chave i18n própria, sem toast
                          // global — mesmo tratamento que o caminho USB dá ao 409
                          // de `capture-biometric`.
                          const conflict =
                            error.status === 409 ? error.error : null;
                          if (typeof conflict?.Outcome === 'string') {
                            patchState(
                              store,
                              {
                                outcome: conflict.Outcome,
                                outcomeMessage: conflict.Message ?? null,
                                percent: 0,
                              },
                              setPropError('enroll', conflict.Outcome)
                            );
                            return;
                          }
                          patchState(
                            store,
                            { percent: 0 },
                            setPropError('enroll', error)
                          );
                          errorStore.setHttpError(error);
                        },
                      })
                    );
                })
              )
            )
          )
        ),

        revokeTemplate: rxMethod<string>(
          pipe(
            tap(() => patchState(store, setPropLoading('revokeTemplate'))),
            switchMap((embeddedTemplateId) =>
              service.revokeTemplate(embeddedTemplateId).pipe(
                tapResponse({
                  next: () =>
                    patchState(
                      store,
                      {
                        templates: store
                          .templates()
                          .filter(
                            (template) =>
                              template.EmbeddedTemplateId !== embeddedTemplateId
                          ),
                      },
                      setPropLoaded('revokeTemplate')
                    ),
                  error: (error: HttpErrorResponse) => {
                    patchState(store, setPropError('revokeTemplate', error));
                    errorStore.setHttpError(error);
                  },
                })
              )
            )
          )
        ),

        /**
         * Pede à placa que pare o cadastro em curso.
         *
         * Sem efeito fora de um cadastro, ou se já foi pedido. Antes de o POST
         * partir não há nada a mandar: o `beginEnroll` vê a marca e não o envia.
         */
        cancelEnroll: (): void => {
          if (!store.enrollLoading() || store.cancelRequested()) return;
          const edgeId = store.edgeId();
          const requestId = store.requestId();
          if (!edgeId || !requestId) return;
          patchState(store, { cancelRequested: true });
          if (store.commandSent()) sendCancel({ edgeId, requestId });
        },

        /** Deixa de acompanhar o equipamento. Nunca para o hub — ele é partilhado. */
        stopWatching: (): void => {
          const edgeId = store.edgeId();
          if (edgeId) void hubService.unsubscribeEdge(edgeId);
          patchState(store, { edgeId: null, requestId: null });
        },

        /** Limpa só o cadastro em curso, preservando a lista já carregada. */
        resetEnroll: (): void => {
          patchState(
            store,
            {
              requestId: null,
              commandSent: false,
              cancelRequested: false,
              stage: null,
              percent: 0,
              outcome: null,
              outcomeMessage: null,
            },
            setPropInit('enroll', 'cancelEnroll')
          );
        },

        reset: (): void => {
          patchState(
            store,
            initialState,
            setPropInit(
              'enroll',
              'cancelEnroll',
              'loadTemplates',
              'revokeTemplate'
            )
          );
        },
      };
    }
  ),
  withHooks({
    onInit(store) {
      const hubService = inject(AccessPointEdgeHubService);
      const destroyRef = inject(DestroyRef);

      hubService.enrollProgress$
        .pipe(takeUntilDestroyed(destroyRef))
        // `untracked` porque o hub emite de forma síncrona na pilha de quem o
        // provocou: sem isso as leituras e o `patchState` daqui herdam o
        // `activeConsumer` desse chamador, e num `computed`/template o Angular
        // recusa a escrita com NG0600.
        .subscribe((event) =>
          untracked(() => {
            // Pode haver mais de um cadastro em curso no mesmo equipamento. Só o
            // que carrega a nossa correlação nos diz respeito; `corrId` vazio é
            // evento de outra natureza e ignora-se em silêncio, não é defeito.
            const requestId = store.requestId();
            if (!requestId || !event.corrId || event.corrId !== requestId) return;

            const stage = event.stage ?? null;
            const percent =
              typeof event.percent === 'number'
                ? event.percent
                : stage
                ? EMBEDDED_ENROLL_STAGE_PERCENT[stage]
                : store.percent();

            patchState(store, { stage, percent });
          })
        );
    },
  })
);
